import { DEFAULT_LANGUAGE, type Language } from './languages';
import { EMPTY_STREAK, type StreakState } from './streak';
import { KEYS, readJson, scopedKey, type StorageAdapter } from './storage';

export interface Profile {
  totalXp: number;
  streak: StreakState;
}

/** Fresh default profile — never a shared singleton callers could mutate. */
function defaultProfile(): Profile {
  return { totalXp: 0, streak: { ...EMPTY_STREAK } };
}

/** XP and streak for one language's games (`scopedKey`). */
export async function loadProfile(
  s: StorageAdapter,
  language: Language = DEFAULT_LANGUAGE,
): Promise<Profile> {
  const p = await readJson<Profile>(s, scopedKey(KEYS.profile, language), defaultProfile());
  // Guard partially-corrupt shapes.
  if (typeof p?.totalXp !== 'number' || typeof p?.streak?.currentStreak !== 'number') {
    return defaultProfile();
  }
  return p;
}

export async function saveProfile(
  s: StorageAdapter,
  p: Profile,
  language: Language = DEFAULT_LANGUAGE,
): Promise<void> {
  await s.set(scopedKey(KEYS.profile, language), JSON.stringify(p));
}

function defaultUuid(): string {
  // crypto.randomUUID exists in Node 20+, browsers, and workers.
  return globalThis.crypto.randomUUID();
}

/**
 * Anonymous per-install id (spec §4.3). Created once, then stable — and
 * shared across languages: it identifies the install, not the game.
 */
export async function getDeviceId(
  s: StorageAdapter,
  gen: () => string = defaultUuid,
): Promise<string> {
  const existing = await s.get(KEYS.deviceId);
  if (existing) return existing;
  const id = gen();
  await s.set(KEYS.deviceId, id);
  return id;
}
